import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  InputNumber,
  message,
  Popconfirm,
  Tag,
  Typography,
  Card,
  Row,
  Col,
  Tooltip,
  Divider,
  Select,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  CopyOutlined,
  PoweroffOutlined,
} from '@ant-design/icons';
import { apiKeyApi, authApi, userApi } from '../../services/api';
import { ApiKey, CreateApiKeyRequest, UpdateApiKeyRequest, TablePaginationConfig, User } from '../../types';
import type { ColumnsType } from 'antd/es/table';
import { useTheme } from '../../context/ThemeContext';
import { requestDeduplication } from '../../utils/requestDeduplication';
import { usePermissions, usePermissionCheck, PermissionGuard } from '../../context/PermissionContext';

const { Title, Text } = Typography;

const ApiKeyManagement: React.FC = () => {
  const { theme } = useTheme();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingApiKey, setEditingApiKey] = useState<ApiKey | null>(null);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [form] = Form.useForm();
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  
  // API密钥显示Modal相关状态
  const [isKeyDisplayModalVisible, setIsKeyDisplayModalVisible] = useState(false);
  const [displayedApiKey, setDisplayedApiKey] = useState<string>('');
  const [keyDisplayTitle, setKeyDisplayTitle] = useState<string>('');

  // 权限检查
  const { loading: permissionLoading } = usePermissions();
  const { canViewAPIs, canCreateAPIs, canEditAPIs, canDeleteAPIs, canRegenerateAPIs } = usePermissionCheck();

  useEffect(() => {
    if (!permissionLoading && canViewAPIs()) {
      fetchCurrentUser();
      fetchUsers();
      fetchApiKeys();
    }
  }, [permissionLoading, pagination.current, pagination.pageSize]);

  const fetchCurrentUser = async () => {
    try {
      const response = await authApi.getCurrentUser();
      console.log('当前用户信息:', response.data);
      setCurrentUser(response.data);
      // 默认选择当前用户
      setSelectedUserId(response.data.id);
    } catch (error) {
      console.error('获取当前用户信息失败:', error);
      message.error('获取当前用户信息失败');
    }
  };

  const fetchUsers = async () => {
    try {
      // 使用请求去重，避免重复调用
      const response = await requestDeduplication.request(
        'getUsersForSelection',
        () => userApi.getUsers({ page: 1, page_size: 100 })
      );
      setUsers(response.data.users);
    } catch (error) {
      console.error('获取用户列表失败:', error);
      // 用户列表获取失败不影响主要功能
    }
  };

  const fetchApiKeys = async () => {
    try {
      setLoading(true);
      const response = await apiKeyApi.getAllApiKeys(pagination.current, pagination.pageSize);
      
      // 处理字段映射，将后端字段映射到前端期望的字段
      const mappedApiKeys = response.data.apis.map((api: any) => ({
        ...api,
        key: api.api_key || api.key,
        status: api.is_active ? 'active' : 'inactive'
      }));
      
      setApiKeys(mappedApiKeys);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
      }));
    } catch (error) {
      message.error('获取API密钥列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingApiKey(null);
    setIsModalVisible(true);
    form.resetFields();
    // 设置默认值，用户ID将自动使用当前用户，请求限制默认为0
    form.setFieldsValue({
      request_limit: 0,
    });
  };

  const handleEdit = (record: ApiKey) => {
    setEditingApiKey(record);
    setIsModalVisible(true);
    form.setFieldsValue({
      name: record.name,
      description: record.description,
    });
  };

  const handleDelete = async (id: number) => {
    try {
      await apiKeyApi.deleteApiKey(id);
      message.success('删除API密钥成功');
      fetchApiKeys();
    } catch (error) {
      message.error('删除API密钥失败');
    }
  };

  const handleRegenerate = async (id: number) => {
    try {
      const response = await apiKeyApi.regenerateApiKey(id);
      
      // 显示重新生成的API密钥
      setDisplayedApiKey(response.data.new_key || '');
      setKeyDisplayTitle('API密钥重新生成成功');
      setIsKeyDisplayModalVisible(true);
      
      fetchApiKeys();
    } catch (error) {
      message.error('重新生成API密钥失败');
    }
  };

  const handleToggleStatus = async (record: ApiKey) => {
    try {
      const newStatus = record.status !== 'active';
      console.log('切换API密钥状态:', { id: record.id, currentStatus: record.status, newStatus });
      await apiKeyApi.toggleApiKeyStatus(record.id, newStatus);
      message.success(`API密钥已${newStatus ? '启用' : '禁用'}`);
      fetchApiKeys();
    } catch (error: any) {
      console.error('切换API密钥状态失败:', error);
      console.error('错误响应:', error.response?.data);
      console.error('错误状态码:', error.response?.status);
      
      let errorMessage = '切换API密钥状态失败';
      if (error.response?.data?.message) {
        errorMessage += `: ${error.response.data.message}`;
      } else if (error.response?.data?.error) {
        errorMessage += `: ${error.response.data.error}`;
      } else if (error.message) {
        errorMessage += `: ${error.message}`;
      }
      
      message.error(errorMessage);
    }
  };

  const handleSubmit = async (values: CreateApiKeyRequest | UpdateApiKeyRequest) => {
    try {
      console.log('提交的数据:', values);
      
      if (editingApiKey) {
        await apiKeyApi.updateApiKey(editingApiKey.id, values as UpdateApiKeyRequest);
        message.success('更新API密钥成功');
      } else {
        // 直接使用当前用户ID
        const userIdToUse = currentUser?.id;
        
        if (!userIdToUse) {
          message.error('无法获取当前用户ID，请重新登录');
          return;
        }
        
        console.log('当前用户信息:', currentUser);
        console.log('使用当前用户ID:', userIdToUse);
        console.log('创建API密钥请求数据:', values);
        
        // 确保用户ID是数字类型
        const numericUserId = typeof userIdToUse === 'string' ? parseInt(userIdToUse, 10) : userIdToUse;
        
        if (isNaN(numericUserId) || numericUserId <= 0) {
          message.error(`无效的用户ID: ${userIdToUse}`);
          return;
        }
        
        console.log('最终数字用户ID:', numericUserId);
        
        // 创建请求数据，移除user_id字段（不应该在请求体中）
        const createRequest = {
          name: values.name,
          description: (values as any).description,
          request_limit: (values as any).request_limit,
        };
        
        console.log('发送到后端的请求数据:', createRequest);
        
        const response = await apiKeyApi.createApiKey(numericUserId, createRequest);
        
        // 显示新创建的API密钥
        setDisplayedApiKey(response.data.api_key || response.data.key || '');
        setKeyDisplayTitle('API密钥创建成功');
        setIsKeyDisplayModalVisible(true);
      }
      setIsModalVisible(false);
      fetchApiKeys();
    } catch (error: any) {
      console.error('API密钥操作失败:', error);
      console.error('错误响应:', error.response?.data);
      console.error('错误状态码:', error.response?.status);
      console.error('完整错误对象:', error);
      
      let errorMessage = editingApiKey ? '更新API密钥失败' : '创建API密钥失败';
      
      if (error.response?.data?.message) {
        errorMessage += `: ${error.response.data.message}`;
      } else if (error.response?.data?.error) {
        errorMessage += `: ${error.response.data.error}`;
      } else if (error.message) {
        errorMessage += `: ${error.message}`;
      }
      
      message.error(errorMessage);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success('已复制到剪贴板');
    }).catch(() => {
      message.error('复制失败');
    });
  };



  const renderApiKey = (key: string, record: ApiKey) => {
    // 始终显示隐藏格式的密钥，符合"密钥只会显示一次"的安全原则
    const displayKey = '••••••••••••••••••••••••••••••••';
    
    return (
      <Text code style={{ maxWidth: 600 }}>
        {displayKey}
      </Text>
    );
  };

  const columns: ColumnsType<ApiKey> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (description: string) => description || '无描述',
    },
    {
      title: 'API密钥',
      dataIndex: 'key',
      key: 'key',
      render: renderApiKey,
      width: 350,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status === 'active' ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '已用次数',
      dataIndex: 'request_count',
      key: 'request_count',
      render: (count: number) => count || 0,
    },
    {
      title: '最后使用',
      dataIndex: 'last_request_at',
      key: 'last_request_at',
      render: (date: string) => date ? new Date(date).toLocaleString('zh-CN') : '从未使用',
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      key: 'action',
      width: 190,
      ellipsis: false,
      fixed: 'right',
      render: (_, record) => (
        <div style={{ textAlign: 'right', padding: '10px' }}>
          <Space size="middle">
            <PermissionGuard permission="api.edit">
              <Tooltip title="编辑">
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(record)}
                />
              </Tooltip>
            </PermissionGuard>
            <PermissionGuard permission="api.regenerate">
              <Tooltip title="重新生成">
                <Popconfirm
                  title="确定要重新生成API密钥吗？"
                  description="重新生成后，原密钥将立即失效"
                  onConfirm={() => handleRegenerate(record.id)}
                  okText="确定"
                  cancelText="取消"
                >
                  <Button size="small" icon={<ReloadOutlined />} />
                </Popconfirm>
              </Tooltip>
            </PermissionGuard>
            <PermissionGuard permission="api.edit">
              <Tooltip title={record.status === 'active' ? '禁用' : '启用'}>
                <Button
                  size="small"
                  icon={<PoweroffOutlined />}
                  type={record.status === 'active' ? 'default' : 'primary'}
                  onClick={() => handleToggleStatus(record)}
                />
              </Tooltip>
            </PermissionGuard>
            <PermissionGuard permission="api.delete">
              <Tooltip title="删除">
                <Popconfirm
                  title="确定要删除这个API密钥吗？"
                  description="删除后无法恢复"
                  onConfirm={() => handleDelete(record.id)}
                  okText="确定"
                  cancelText="取消"
                >
                  <Button size="small" icon={<DeleteOutlined />} danger />
                </Popconfirm>
              </Tooltip>
            </PermissionGuard>
          </Space>
        </div>
      ),
    },
  ];

  // 权限检查失败时的展示
  if (!canViewAPIs()) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Title level={3}>权限不足</Title>
          <p>您没有查看API密钥管理的权限</p>
        </div>
      </Card>
    );
  }

  return (
    <div>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={2} style={{ margin: 0, color: theme === 'dark' ? '#ffffff' : '#000000' }}>
              API密钥管理
            </Title>
            <Text type="secondary" style={{ color: theme === 'dark' ? '#999999' : undefined }}>
              管理您的API访问密钥，用于调用API服务
            </Text>
          </Col>
          <Col>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={fetchApiKeys}>
                刷新
              </Button>
              <PermissionGuard permission="api.create">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleCreate}
                >
                  新增API密钥
                </Button>
              </PermissionGuard>
            </Space>
          </Col>
        </Row>

        <Divider />

        {/* 使用提示 */}
        <Card 
          size="small" 
          style={{ 
            marginBottom: 16, 
            background: theme === 'dark' ? '#1a3d1a' : '#f6ffed',
            border: theme === 'dark' ? '1px solid #2d5a2d' : '1px solid #b7eb8f'
          }}
        >
          <Row gutter={16}>
            <Col span={24}>
              <Space direction="vertical" size="small">
                <Text strong style={{ color: theme === 'dark' ? '#ffffff' : '#000000' }}>使用说明：</Text>
                <Text style={{ color: theme === 'dark' ? '#cccccc' : '#000000' }}>• 创建API密钥后，请妥善保管，密钥只会在创建和重新生成时完整显示一次</Text>
                <Text style={{ color: theme === 'dark' ? '#cccccc' : '#000000' }}>• 列表中的密钥以隐藏格式显示，确保密钥安全性</Text>
                <Text style={{ color: theme === 'dark' ? '#cccccc' : '#000000' }}>• 可以为不同的应用场景创建多个API密钥，便于管理和追踪</Text>
                <Text style={{ color: theme === 'dark' ? '#cccccc' : '#000000' }}>• 如发现密钥泄露，请立即禁用或重新生成密钥</Text>
                <Text style={{ color: theme === 'dark' ? '#cccccc' : '#000000' }}>• 定期检查API密钥的使用情况，及时清理不再使用的密钥</Text>
              </Space>
            </Col>
          </Row>
        </Card>

        <Table
          columns={columns}
          dataSource={apiKeys}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `共 ${total} 条记录，当前显示第 ${range[0]}-${range[1]} 条`,
            onChange: (page, pageSize) => {
              setPagination({ current: page, pageSize: pageSize || 10, total: pagination.total });
            },
          }}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      <Modal
        title={editingApiKey ? '编辑API密钥' : '新增API密钥'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            label="名称"
            name="name"
            rules={[
              { required: true, message: '请输入API密钥名称' },
              { max: 50, message: '名称长度不能超过50个字符' },
            ]}
          >
            <Input placeholder="请输入API密钥名称，例如：生产环境密钥" />
          </Form.Item>

          <Form.Item
            label="描述"
            name="description"
            rules={[
              { max: 500, message: '描述长度不能超过500个字符' },
            ]}
          >
            <Input.TextArea 
              placeholder="请输入API密钥描述（可选）" 
              rows={3}
              maxLength={500}
            />
          </Form.Item>

          {/* 隐藏的请求限制字段，默认值为0 */}
          <Form.Item
            name="request_limit"
            initialValue={0}
            style={{ display: 'none' }}
          >
            <InputNumber />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setIsModalVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                {editingApiKey ? '更新' : '创建'}
              </Button>
            </Space>
          </Form.Item>
                  </Form>
        </Modal>

        {/* API密钥显示Modal */}
        <Modal
          title={keyDisplayTitle}
          open={isKeyDisplayModalVisible}
          onCancel={() => setIsKeyDisplayModalVisible(false)}
          footer={[
            <Button key="cancel" onClick={() => setIsKeyDisplayModalVisible(false)}>
              关闭
            </Button>,
            <Button 
              key="copy" 
              type="primary" 
              onClick={() => {
                copyToClipboard(displayedApiKey);
                setIsKeyDisplayModalVisible(false);
              }}
            >
              复制并关闭
            </Button>
          ]}
          width={600}
        >
          <div>
            <p style={{ color: theme === 'dark' ? '#ffffff' : '#000000' }}>
              {keyDisplayTitle.includes('创建') ? '新的API密钥已创建，请及时保存：' : '新的API密钥已生成，请及时保存：'}
            </p>
            <div style={{ 
              background: theme === 'dark' ? '#2f2f2f' : '#f5f5f5', 
              padding: '12px', 
              borderRadius: '4px',
              wordBreak: 'break-all',
              fontFamily: 'monospace',
              color: theme === 'dark' ? '#ffffff' : '#000000',
              border: theme === 'dark' ? '1px solid #434343' : '1px solid #d9d9d9'
            }}>
              {displayedApiKey}
            </div>
            <p style={{ marginTop: '12px', color: theme === 'dark' ? '#999999' : '#666' }}>
              注意：此密钥只会显示一次，请立即复制保存。
            </p>
          </div>
        </Modal>
      </div>
    );
  };

  export default ApiKeyManagement; 